import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Candle } from "@/components/Candle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { titleCaseName } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCountdown, breakdownTime } from "@/hooks/useCountdown";
import { toast } from "sonner";
import { Flame, Heart, Share2, Clock, Loader2, Send, Pencil, ImagePlus, X, Facebook, Mail, Instagram, Infinity as InfinityIcon, SmilePlus, ArrowLeft, Globe, MessageCircle } from "lucide-react";
import { SOCIAL_PLATFORMS, hasSocials } from "@/lib/socials";
import { setPageSeo, resetPageSeo, SEO_BASE } from "@/lib/seo";
import { PlanSelector } from "@/components/PlanSelector";
import { AuthDialog } from "@/components/AuthDialog";
import { RisingPhotos } from "@/components/RisingPhotos";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { ringStyle } from "@/lib/flagRing";
import { FlagBadge } from "@/components/FlagBadge";
import { PhotoCropDialog } from "@/components/PhotoCropDialog";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resizeImage = (file, max = 1200, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > max || height > max) {
        if (width >= height) { height = Math.round((height * max) / width); width = max; }
        else { width = Math.round((width * max) / height); height = max; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

const CountdownPill = ({ value, label }) => (
  <div className="flex min-w-[62px] flex-col items-center rounded-2xl border border-white/10 bg-card/60 px-3 py-2 sm:min-w-[74px] sm:px-4">
    <span className="text-2xl font-semibold tabular-nums text-amber-300 sm:text-3xl">{String(value).padStart(2, "0")}</span>
    <span className="mt-0.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
  </div>
);

const REACTION_EMOJIS = ["❤️", "🙏", "😢", "🕊️", "🌹", "🤗"];

const fmtDMY = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "");

const LOCALE_MAP = { es_mx: "es-MX", fil: "fil", zh: "zh-CN" };
const toLocale = (lang) => LOCALE_MAP[lang] || lang || "fr";
const timeAgo = (dateStr, lang) => {
  try {
    const rtf = new Intl.RelativeTimeFormat(toLocale(lang), { numeric: "auto" });
    const diff = (new Date(dateStr).getTime() - Date.now()) / 1000;
    const units = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60]];
    for (const [unit, sec] of units) {
      if (Math.abs(diff) >= sec) return rtf.format(Math.round(diff / sec), unit);
    }
    return rtf.format(Math.round(diff), "second");
  } catch { return new Date(dateStr).toLocaleDateString(); }
};
const fullDate = (dateStr, lang) => { try { return new Date(dateStr).toLocaleString(toLocale(lang)); } catch { return new Date(dateStr).toLocaleString(); } };

const AVATAR_COLORS = ["bg-rose-500", "bg-sky-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500", "bg-teal-500", "bg-fuchsia-500", "bg-indigo-500"];
const avatarColor = (name) => AVATAR_COLORS[[...(name || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const avatarInitial = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

const getVisitorId = () => {
  let v = localStorage.getItem("bdc_visitor_id");
  if (!v) {
    v = (window.crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("bdc_visitor_id", v);
  }
  return v;
};

export default function Memorial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { format, currency } = useCurrency();
  const { t, i18n } = useTranslation();
  const editFileRef = useRef();
  const [candle, setCandle] = useState(null);
  const [condolences, setCondolences] = useState([]);
  const [price, setPrice] = useState(100);
  const [plans, setPlans] = useState([]);
  const [eternityPrice, setEternityPrice] = useState(2900);
  const [extraPlans, setExtraPlans] = useState([]);
  const [morePlansOpen, setMorePlansOpen] = useState(false);
  const [morePlan, setMorePlan] = useState("1mois");
  const [relightOpen, setRelightOpen] = useState(false);
  const [relightPlan, setRelightPlan] = useState("decouverte");
  const [author, setAuthor] = useState("");
  const [authorLast, setAuthorLast] = useState("");
  const [message, setMessage] = useState("");
  const [condPhotos, setCondPhotos] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const condPhotoRef = useRef();
  const [sending, setSending] = useState(false);
  const [relighting, setRelighting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [eName, setEName] = useState("");
  const [eMsg, setEMsg] = useState("");
  const [ePhoto, setEPhoto] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [eBirth, setEBirth] = useState("");
  const [eDeath, setEDeath] = useState("");
  const [eSocials, setESocials] = useState({});
  const [saving, setSaving] = useState(false);
  const visitorId = useRef("");
  useEffect(() => { visitorId.current = getVisitorId(); }, []);
  const [pickerOpenId, setPickerOpenId] = useState(null);

  const react = async (condId, emoji) => {
    setPickerOpenId(null);
    try {
      const r = await api.post(`/condolences/${condId}/react`, { emoji, reactor_id: visitorId.current });
      setCondolences((list) => list.map((c) => (c.id === condId ? { ...c, reactions: r.data.reactions } : c)));
    } catch (e) { toast.error(t("memorial.error")); }
  };

  const load = useCallback(async () => {
    try {
      const [c, cond, s] = await Promise.all([
        api.get(`/candles/${id}`),
        api.get(`/candles/${id}/condolences`),
        api.get(`/settings`),
      ]);
      setCandle(c.data);
      setCondolences(cond.data);
      setPrice(s.data.price_cents);
      setPlans(s.data.plans || []);
      if (s.data.eternity_price_cents != null) setEternityPrice(s.data.eternity_price_cents);
      if (s.data.extra_plans?.length) { setExtraPlans(s.data.extra_plans); setMorePlan(s.data.extra_plans[0].code); }
    } catch (e) {
      toast.error(t("memorial.notFound"));
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!candle) return;
    const by = (candle.birth_date || "").slice(0, 4) || (candle.source_years || {}).birth;
    const dy = (candle.death_date || "").slice(0, 4) || (candle.source_years || {}).death;
    const span = by && dy ? ` (${by}–${dy})` : "";
    const name = candle.deceased_name || "";
    const title = `En mémoire de ${name}${span} | BougieDuCœur`;
    const description = (candle.tribute_message
      ? candle.tribute_message.replace(/\s+/g, " ").trim().slice(0, 155)
      : `Allumez une bougie et laissez un message gratuit en mémoire de ${name}.`);
    const image = `${SEO_BASE}/api/candles/${candle.id}/og-image?fmt=wide`;
    const canonicalPath = `/candle/${candle.id}`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Person",
      name,
      ...(candle.birth_date ? { birthDate: candle.birth_date } : {}),
      ...(candle.death_date ? { deathDate: candle.death_date } : {}),
      ...(candle.country?.name || candle.nationality ? { nationality: candle.country?.name || candle.nationality } : {}),
      image,
      url: `${SEO_BASE}${canonicalPath}`,
      subjectOf: { "@type": "WebPage", name: title, inLanguage: "fr" },
    };
    setPageSeo({ title, description, canonicalPath, image, jsonLd });
    return () => resetPageSeo();
  }, [candle]);

  const { remaining, progress, expired } = useCountdown(candle?.expires_at, candle?.burn_hours);

  const buildShareUrl = () => `${API}/s/${candle?.share_slug || id}`;
  const shareMessage = () => `🕯️ ${t("memorial.inMemory")} ${candle.deceased_name} — ${t("memorial.shareInvite", { defaultValue: "Allumez une bougie et laissez-lui un message gratuit 🕯️" })}`;
  const trackShare = () => {
    if (user && candle && user.id === candle.owner_id) {
      api.post("/me/share-track").catch(() => {});
    }
  };

  const share = async () => {
    const url = buildShareUrl();
    const title = `${t("memorial.inMemory")} ${candle.deceased_name}`;
    const text = shareMessage();
    trackShare();
    try {
      try {
        const imgSrc = candle.photo_base64 || "/candle.png";
        const resp = await fetch(imgSrc);
        const blob = await resp.blob();
        const ext = (blob.type && blob.type.split("/")[1]) || "png";
        const file = new File([blob], `${candle.deceased_name || "hommage"}.${ext}`, { type: blob.type || "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title, text: `${text}\n${url}`, url, files: [file] });
          return;
        }
      } catch (e) { /* fallback below */ }
      if (navigator.share) await navigator.share({ title, text, url });
      else { await navigator.clipboard.writeText(`${text}\n${url}`); toast.success(t("memorial.linkCopied")); }
    } catch (e) { /* cancelled */ }
  };

  const copyLink = async () => {
    trackShare();
    await navigator.clipboard.writeText(`${shareMessage()}\n${buildShareUrl()}`);
    toast.success(t("memorial.copiedToClipboard"));
  };

  const socialShare = (network) => {
    trackShare();
    const raw = buildShareUrl();
    const url = encodeURIComponent(raw);
    const msg = shareMessage();
    const text = encodeURIComponent(msg);
    if (network === "instagram") {
      const shareData = {
        title: `${t("memorial.inMemory")} ${candle.deceased_name}`,
        text: `${msg}\n${raw}`,
        url: raw,
      };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        navigator.clipboard.writeText(`${msg}\n${raw}`);
        toast.success(t("memorial.instagramCopied"));
      }
      return;
    }
    const map = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      email: `mailto:?subject=${encodeURIComponent(t("memorial.inMemory") + " " + candle.deceased_name)}&body=${text}%0A%0A${url}`,
    };
    if (network === "sms") {
      const isApple = /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent || "");
      window.location.href = `${isApple ? "sms:&body=" : "sms:?&body="}${encodeURIComponent(`${msg}\n${raw}`)}`;
      return;
    }
    if (network === "email") { window.location.href = map.email; return; }
    window.open(map[network], "_blank", "noopener,noreferrer");
  };

  const submitCondolence = async (e) => {
    e.preventDefault();
    if (!author.trim() || !message.trim()) return;
    setSending(true);
    try {
      const fullName = `${author} ${authorLast}`.trim();
      const res = await api.post(`/candles/${id}/condolences`, { author_name: fullName, message, photos: condPhotos });
      setMessage("");
      setAuthor("");
      setAuthorLast("");
      setCondPhotos([]);
      toast.success(t("memorial.messageDeposited"));
      setCondolences((list) => [res.data, ...list]);
      setCandle((c) => ({ ...c, condolence_count: (c.condolence_count || 0) + 1 }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.sendError"));
    } finally {
      setSending(false);
    }
  };

  const handleAddPhotosClick = () => {
    if (!user) { setAuthOpen(true); return; }
    condPhotoRef.current?.click();
  };

  const onCondPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = 4 - condPhotos.length;
    const results = [];
    for (const f of files.slice(0, room)) {
      try { results.push(await resizeImage(f)); } catch (err) { /* skip */ }
    }
    setCondPhotos((p) => [...p, ...results].slice(0, 4));
  };

  const relight = async (planCode) => {
    const plan = typeof planCode === "string" ? planCode : relightPlan;
    setRelighting(true);
    try {
      const res = await api.post(`/candles/${id}/relight`, { origin_url: window.location.origin, author_name: author || null, plan, currency });
      if (submitPayment(res.data)) {
        return;
      } else {
        toast.success(t("memorial.messageUpdated"));
        setRelightOpen(false);
        load();
        setRelighting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
      setRelighting(false);
    }
  };

  const openEdit = () => {
    setEName(candle.deceased_name || "");
    setEMsg(candle.tribute_message || "");
    setEPhoto(candle.photo_base64 || null);
    setEBirth(candle.birth_date || "");
    setEDeath(candle.death_date || "");
    setESocials(candle.socials || {});
    setEditOpen(true);
  };

  const onEditFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 40 * 1024 * 1024) return toast.error(t("create.photoTooBig"));
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    if (!eName.trim()) return toast.error(t("memorial.nameRequired"));
    setSaving(true);
    try {
      const res = await api.patch(`/candles/${id}`, {
        deceased_name: eName,
        tribute_message: eMsg,
        photo_base64: ePhoto ?? "",
        birth_date: eBirth || null,
        death_date: eDeath || null,
        socials: eSocials || {},
      });
      setCandle(res.data);
      setEditOpen(false);
      toast.success(t("memorial.messageUpdated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.editError"));
    } finally {
      setSaving(false);
    }
  };

  if (!candle) {
    return <div className="flex h-[100dvh] items-center justify-center"><Loader2 className="animate-spin text-sky-400" /></div>;
  }

  const isPaid = candle.paid;
  const isEternal = candle.eternal;
  const showExtinguished = isPaid && !isEternal && expired;
  const isOwner = user && candle && user.id === candle.owner_id;
  const showEternity = isEternal || (isPaid && !expired && remaining > 365 * 24 * 3600);

  return (
    <div className="min-h-[100dvh] bg-black">
      <Header />

      {/* Back to home button */}
      <div className="px-4 pt-4 sm:px-6">
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="rounded-full border-white/15 bg-card/50 text-sm text-foreground hover:border-sky-400 hover:text-sky-400"
          data-testid="memorial-back-btn"
        >
          <ArrowLeft size={16} className="mr-2" /> {t("nav.home")}
        </Button>
      </div>

      {/* Candle hero */}
      <div className="relative flex flex-col items-center px-5 pt-4 pb-6 text-center">
        {candle.photo_base64 && (() => {
          const ring = ringStyle(candle.flag_colors);
          return (
            <div className={`relative mb-4 inline-block rounded-full ${ring ? "p-[4px] shadow-[0_0_20px_rgba(0,0,0,0.5)]" : ""}`} style={ring || undefined} title={candle.country_name || ""} data-testid="nationality-ring">
              <img src={candle.photo_base64} alt={candle.deceased_name} className={`h-24 w-24 rounded-full object-cover ${ring ? "border-2 border-black/60" : "border-2 border-amber-300/80 shadow-[0_0_24px_rgba(251,191,36,0.35)]"}`} data-testid="deceased-photo" />
              <FlagBadge flag={candle.flag} name={candle.country_name} className="h-7 w-7 border-[3px]" />
            </div>
          );
        })()}
        <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground">{t("memorial.inMemory")}</p>
        <h1 className="mt-1 text-3xl font-medium sm:text-4xl" data-testid="deceased-name">{candle.deceased_name}</h1>
        {(candle.birth_date || candle.death_date) && (
          <p className="mt-1 text-base tracking-wide text-amber-200/80 sm:text-lg" data-testid="deceased-dates">
            {fmtDMY(candle.birth_date)}{candle.birth_date && candle.death_date ? "  —  " : ""}{fmtDMY(candle.death_date)}
          </p>
        )}
        {candle.tribute_message && (
          <p className="mt-3 max-w-lg text-base font-normal italic leading-relaxed text-white/95 sm:text-lg" data-testid="tribute-message">« {candle.tribute_message} »</p>
        )}

        <div className="relative my-4">
          <RisingPhotos contained items={condolences.flatMap((c) => (c.photo_urls || []).map((u, i) => ({ key: `${c.id}-${i}`, url: `${BACKEND}${u}`, label: c.author_name }))).slice(0, 20)} />
          <Candle progress={isPaid ? (isEternal ? 1 : progress) : 1} extinguished={!isPaid || showExtinguished} size={340} />
        </div>

        {isPaid ? (
          showEternity ? (
            <div className="flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-2 text-sky-300" data-testid="countdown">
              <InfinityIcon size={16} />
              <span className="font-medium">{t("memorial.forEternity")}</span>
            </div>
          ) : expired ? (
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-card/50 px-4 py-2" data-testid="countdown">
              <Clock size={16} className="text-muted-foreground" />
              <span className="font-medium">{t("memorial.extinguished")}</span>
            </div>
          ) : (
            <div className="flex items-end justify-center gap-2 sm:gap-3" aria-live="polite" data-testid="countdown">
              <CountdownPill value={breakdownTime(remaining).d} label={t("memorial.days")} />
              <CountdownPill value={breakdownTime(remaining).h} label={t("memorial.hours")} />
              <CountdownPill value={breakdownTime(remaining).m} label={t("memorial.minutes")} />
              <CountdownPill value={breakdownTime(remaining).s} label={t("memorial.seconds")} />
            </div>
          )
        ) : (
          <div className="rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-2 text-sm text-amber-300" data-testid="pending-payment-badge">
            {t("memorial.pendingPayment")}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {isOwner && (
            <Button variant="outline" className="rounded-full border-white/15" onClick={openEdit} data-testid="edit-candle-btn">
              <Pencil size={15} className="mr-2" /> {t("memorial.edit")}
            </Button>
          )}
          <Button variant="outline" className="rounded-full border-white/15" onClick={share} data-testid="share-btn">
            <Share2 size={16} className="mr-2" /> {t("memorial.share")}
          </Button>
          {candle.socials?.website && (
            <a href={candle.socials.website} target="_blank" rel="noreferrer" data-testid="official-site-btn">
              <Button className="rounded-full bg-amber-400 text-black hover:bg-amber-300">
                <Globe size={16} className="mr-2" /> {t("memorial.officialSite")}
              </Button>
            </a>
          )}
          <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={copyLink} data-testid="copy-link-btn">
            {t("memorial.copyLink")}
          </Button>
        </div>

        {/* Réseaux sociaux */}
        <div className="mt-3 flex items-center justify-center gap-2" data-testid="social-share">
          <button
            type="button"
            onClick={() => socialShare("whatsapp")}
            aria-label="Partager sur WhatsApp"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-[#25D366] hover:text-[#25D366]"
            data-testid="share-whatsapp"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
          </button>
          <button
            type="button"
            onClick={() => socialShare("facebook")}
            aria-label="Partager sur Facebook"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-[#1877F2] hover:text-[#1877F2]"
            data-testid="share-facebook"
          >
            <Facebook size={18} />
          </button>
          <button
            type="button"
            onClick={() => socialShare("x")}
            aria-label="Partager sur X"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-white hover:text-white"
            data-testid="share-x"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </button>
          <button
            type="button"
            onClick={() => socialShare("instagram")}
            aria-label="Partager sur Instagram"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-[#E4405F] hover:text-[#E4405F]"
            data-testid="share-instagram"
          >
            <Instagram size={18} />
          </button>
          <button
            type="button"
            onClick={() => socialShare("sms")}
            aria-label="Partager par SMS"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-emerald-400 hover:text-emerald-400"
            data-testid="share-sms"
          >
            <MessageCircle size={18} />
          </button>
          <button
            type="button"
            onClick={() => socialShare("email")}
            aria-label="Partager par email"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/50 text-muted-foreground transition-colors hover:border-sky-400 hover:text-sky-400"
            data-testid="share-email"
          >
            <Mail size={18} />
          </button>
        </div>

        {/* Réseaux sociaux DU DÉFUNT (distinct du partage) */}
        {hasSocials(candle.socials) && (
          <div className="mt-6 w-full max-w-lg border-t border-white/10 pt-5" data-testid="deceased-socials">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("memorial.findThemOn", { name: candle.deceased_name, defaultValue: "Retrouvez {{name}} sur" })}</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
              {SOCIAL_PLATFORMS.filter((p) => candle.socials?.[p.key]).map((p) => (
                <a
                  key={p.key}
                  href={candle.socials[p.key]}
                  target="_blank"
                  rel="noreferrer"
                  title={p.label}
                  aria-label={p.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-card/50 transition-all hover:scale-110"
                  style={{ color: p.color }}
                  data-testid={`deceased-social-${p.key}`}
                >
                  <p.Icon size={19} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Relight banner */}
      {(expired || progress < 0.34) && isPaid && !isEternal && (
        <div className="mx-auto mb-4 max-w-lg px-5">
          <div className="glass rounded-2xl border border-sky-400/20 p-4 text-center fade-in" data-testid="relight-banner">
            <p className="text-sm text-muted-foreground">{expired ? t("memorial.flameExtinguished") : t("memorial.flameWeak")} {t("memorial.reviveInvite")}</p>
          </div>
        </div>
      )}

      {/* Condolences */}
      <div className="mx-auto max-w-lg px-5 pb-64">
        <div className="rounded-3xl border border-amber-300/15 bg-amber-300/[0.02] p-4 shadow-[0_0_60px_-20px_rgba(252,211,77,0.25)] sm:p-5" data-testid="condolences-section">
          {/* Section band */}
          <div className="mb-6 text-center" data-testid="condolences-header">
            <div className="flex items-center justify-center gap-2 text-amber-300">
              <Heart size={16} className="fill-amber-300/20" />
              <h2 className="text-base font-medium uppercase tracking-[0.18em]">{t("memorial.condolences")}</h2>
            </div>
            <div className="mx-auto mt-3 h-px w-24 bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
            <p className="mt-3 text-sm text-muted-foreground" data-testid="condolences-count">
              <span className="text-xl font-semibold tabular-nums text-foreground">{candle.condolence_count || 0}</span>{" "}
              {t("memorial.supportCountLabel")}
            </p>
          </div>

          {/* Leave a message block */}
          <div className="mb-6 rounded-2xl border border-amber-300/20 bg-card/50 p-4" data-testid="leave-message-block">
            <div className="mb-0.5 flex items-center gap-2">
              <Send size={15} className="text-amber-300" />
              <h3 className="text-sm font-semibold text-foreground">{t("memorial.leaveMessage")}</h3>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{t("memorial.leaveMessageSub")}</p>
            <form onSubmit={submitCondolence} className="space-y-2" data-testid="condolence-form">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder={t("auth.firstName")} value={author} onChange={(e) => setAuthor(titleCaseName(e.target.value))} data-testid="condolence-author" />
                <Input placeholder={t("auth.lastName")} value={authorLast} onChange={(e) => setAuthorLast(titleCaseName(e.target.value))} data-testid="condolence-author-last" />
              </div>
              <Textarea placeholder={t("memorial.supportMessage")} rows={5} className="min-h-[130px] resize-y text-base" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="condolence-message" />
              <input ref={condPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={onCondPhotos} />
              {condPhotos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {condPhotos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="h-16 w-16 rounded-lg object-cover" />
                      <button type="button" onClick={() => setCondPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5" data-testid={`cond-photo-remove-${i}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" variant="outline" size="sm" onClick={handleAddPhotosClick} disabled={condPhotos.length >= 4} className="w-full rounded-full border-white/15" data-testid="cond-add-photo-btn">
                <ImagePlus size={15} className="mr-1.5" /> {t("create.addPhoto")} {condPhotos.length > 0 ? `(${condPhotos.length}/4)` : "· max 4"}
              </Button>
              <Button type="submit" disabled={sending} className="w-full rounded-full bg-amber-300 font-semibold text-black hover:bg-amber-200" data-testid="condolence-submit">
                {sending ? <Loader2 className="animate-spin" size={16} /> : <><Send size={15} className="mr-2" /> {t("memorial.depositFree")}</>}
              </Button>
            </form>
          </div>

          {/* Messages list */}
          <div className="space-y-3">
          {condolences.map((c) => (
            <div key={c.id} className="rounded-2xl border border-white/10 bg-card/30 p-4 fade-in" data-testid="condolence-item">
              <div className="flex items-start gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor(c.author_name)}`} data-testid="condolence-avatar">{avatarInitial(c.author_name)}</div>
                <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-amber-200">{c.author_name}</span>
                <span className="shrink-0 text-xs text-muted-foreground" title={fullDate(c.created_at, i18n.language)} data-testid="condolence-date">{timeAgo(c.created_at, i18n.language)}</span>
              </div>
              <p className="mt-1 whitespace-pre-line break-words text-sm font-light text-foreground/90">{c.message}</p>
              {c.photo_urls && c.photo_urls.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="condolence-photos">
                  {c.photo_urls.map((u, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setLightbox({ photos: c.photo_urls.map((x) => `${BACKEND}${x}`), index: i })}
                      className="block"
                      data-testid={`condolence-photo-${i}`}
                    >
                      <img src={`${BACKEND}${u}`} alt="" className="h-20 w-20 cursor-pointer rounded-lg border border-white/10 object-cover transition-transform hover:scale-105" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="reaction-bar">
                {REACTION_EMOJIS.filter((emo) => ((c.reactions && c.reactions[emo]) || []).length > 0).map((emo) => {
                  const users = c.reactions[emo] || [];
                  const mine = users.includes(visitorId.current);
                  return (
                    <button
                      key={emo}
                      type="button"
                      onClick={() => react(c.id, emo)}
                      aria-label={`Réaction ${emo}`}
                      className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm transition-colors ${mine ? "border-amber-300/70 bg-amber-300/10" : "border-white/10 hover:border-white/30"}`}
                      data-testid={`reaction-count-${emo}`}
                    >
                      <span className="leading-none">{emo}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">{users.length}</span>
                    </button>
                  );
                })}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setPickerOpenId((id) => (id === c.id ? null : c.id))}
                    aria-label="Ajouter une réaction"
                    className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-muted-foreground transition-colors hover:border-sky-400 hover:text-sky-400"
                    data-testid={`reaction-trigger-${c.id}`}
                  >
                    <SmilePlus size={16} />
                  </button>
                  {pickerOpenId === c.id && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setPickerOpenId(null)} />
                      <div className="absolute top-full left-0 z-50 mt-1.5 flex items-center gap-1 rounded-full border border-white/15 bg-card/95 px-2 py-1.5 shadow-xl backdrop-blur fade-in" data-testid={`reaction-picker-${c.id}`}>
                        {REACTION_EMOJIS.map((emo, i) => {
                          const users = (c.reactions && c.reactions[emo]) || [];
                          const mine = users.includes(visitorId.current);
                          return (
                            <button
                              key={emo}
                              type="button"
                              onClick={() => react(c.id, emo)}
                              aria-label={`Réagir ${emo}`}
                              className={`flex h-9 w-9 items-center justify-center rounded-full text-xl transition-transform hover:scale-125 ${mine ? "bg-amber-300/20" : "hover:bg-white/10"}`}
                              data-testid={`reaction-btn-${i}`}
                            >
                              {emo}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
                </div>
              </div>
            </div>
          ))}
          {condolences.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="condolences-empty">
              <Heart size={30} className="text-amber-300/40" />
              <p className="text-sm text-muted-foreground">{t("memorial.firstMessage")}</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Sticky relight CTA */}
      {!isEternal && (
        <div className={`pointer-events-none fixed inset-x-0 z-30 px-5 py-3 ${user ? "bottom-[calc(84px+env(safe-area-inset-bottom))]" : "bottom-0"}`}>
          <div className="pointer-events-auto mx-auto max-w-lg space-y-2">
            <Button onClick={() => relight("decouverte")} disabled={relighting} className={`w-full rounded-full py-3 text-sm font-semibold transition-colors ${showExtinguished ? "border border-sky-400 bg-sky-400 text-black hover:bg-sky-300" : "bg-black text-amber-300 ring-1 ring-amber-300/50 shadow-[0_4px_20px_0_rgba(0,0,0,0.6)] hover:bg-neutral-900"}`} data-testid="relight-btn">
              {relighting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Flame size={16} className="mr-2" />} {t("memorial.relightCta")} ({format(price)})
            </Button>
            {isPaid && (
              <Button onClick={() => setMorePlansOpen(true)} className="w-full rounded-full border border-sky-400 bg-sky-400 py-3 text-sm font-semibold text-black hover:bg-sky-300" data-testid="relight-eternity-btn">
                <InfinityIcon size={16} className="mr-2" /> {t("memorial.extendMore")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Relight plan dialog */}
      <Dialog open={relightOpen} onOpenChange={setRelightOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="relight-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{t("memorial.relightTitle")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{t("memorial.relightDesc", { name: candle.deceased_name })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <PlanSelector plans={plans} value={relightPlan} onChange={setRelightPlan} />
            <Button onClick={() => relight()} disabled={relighting} className="w-full rounded-full bg-black text-amber-300 ring-1 ring-amber-300/50 hover:bg-neutral-900" data-testid="relight-confirm-btn">
              {relighting ? <Loader2 className="animate-spin" size={16} /> : <><Flame size={16} className="mr-2" /> {t("memorial.relightCta")} ({format(plans.find((p) => p.code === relightPlan)?.price_cents ?? price)})</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prolongé Plus : 4 durées supplémentaires */}
      <Dialog open={morePlansOpen} onOpenChange={setMorePlansOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="more-plans-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{t("memorial.extendMore")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{t("memorial.relightDesc", { name: candle.deceased_name })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <PlanSelector plans={extraPlans} value={morePlan} onChange={setMorePlan} />
            <Button onClick={() => relight(morePlan)} disabled={relighting} className="w-full rounded-full bg-black text-amber-300 ring-1 ring-amber-300/50 hover:bg-neutral-900" data-testid="more-plans-confirm-btn">
              {relighting ? <Loader2 className="animate-spin" size={16} /> : <><Flame size={16} className="mr-2" /> {t("memorial.relightCta")} ({format(extraPlans.find((p) => p.code === morePlan)?.price_cents ?? eternityPrice)})</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-candle-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{t("memorial.editTitle")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{t("memorial.editDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label htmlFor="e-name">{t("create.nameLabel")}</Label>
              <Input id="e-name" value={eName} onChange={(e) => setEName(titleCaseName(e.target.value))} data-testid="edit-name-input" />
            </div>
            <div>
              <Label htmlFor="e-msg">{t("create.messageLabel")}</Label>
              <Textarea id="e-msg" rows={4} value={eMsg} onChange={(e) => setEMsg(e.target.value)} data-testid="edit-message-input" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <Label htmlFor="e-birth" className="text-xs text-muted-foreground">{t("create.birthDate")}</Label>
                <Input id="e-birth" type="date" value={eBirth} onChange={(e) => setEBirth(e.target.value)} className="mt-1 min-w-0 max-w-full appearance-none" data-testid="edit-birth-date" />
              </div>
              <div className="min-w-0">
                <Label htmlFor="e-death" className="text-xs text-muted-foreground">{t("create.deathDate")}</Label>
                <Input id="e-death" type="date" value={eDeath} onChange={(e) => setEDeath(e.target.value)} className="mt-1 min-w-0 max-w-full appearance-none" data-testid="edit-death-date" />
              </div>
            </div>
            <div>
              <Label>{t("memorial.photo")}</Label>
              {ePhoto ? (
                <div className="relative mt-1 w-fit">
                  <img src={ePhoto} alt="preview" className="h-24 w-24 rounded-full border-2 border-amber-300/80 object-cover shadow-[0_0_20px_rgba(251,191,36,0.3)]" />
                  <button type="button" onClick={() => setEPhoto(null)} className="absolute -right-2 -top-2 rounded-full bg-destructive p-1" data-testid="edit-remove-photo-btn">
                    <X size={14} />
                  </button>
                  <button type="button" onClick={() => setCropSrc(ePhoto)} className="mt-2 block text-xs text-sky-400 underline hover:text-sky-300" data-testid="edit-reposition-photo-btn">
                    {t("cropper.edit")}
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => editFileRef.current?.click()} className="mt-1 flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-dashed border-amber-300/40 text-muted-foreground transition-colors hover:border-amber-300 hover:text-amber-300" data-testid="edit-add-photo-btn">
                  <ImagePlus size={20} />
                  <span className="mt-1 text-xs">{t("memorial.add")}</span>
                </button>
              )}
              <input ref={editFileRef} type="file" accept="image/*" className="hidden" onChange={onEditFile} />
              <PhotoCropDialog open={!!cropSrc} image={cropSrc} onCancel={() => setCropSrc(null)} onCropped={(b64) => { setEPhoto(b64); setCropSrc(null); }} />
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="edit-socials">
              <Label className="text-amber-300">{t("memorial.socialsTitle", { defaultValue: "Réseaux sociaux du défunt (optionnel)" })}</Label>
              <p className="mt-1 mb-2 text-xs text-muted-foreground">{t("memorial.socialsHint", { defaultValue: "Ajoutez les liens officiels — ils apparaîtront sur la fiche." })}</p>
              <div className="space-y-2">
                {SOCIAL_PLATFORMS.map((p) => (
                  <div key={p.key} className="flex items-center gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${p.color}22`, color: p.color }}><p.Icon size={15} /></span>
                    <Input
                      value={eSocials[p.key] || ""}
                      onChange={(e) => setESocials((s) => ({ ...s, [p.key]: e.target.value }))}
                      placeholder={p.placeholder}
                      className="h-9 text-sm"
                      data-testid={`edit-social-${p.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={saveEdit} disabled={saving} className="w-full rounded-full" data-testid="edit-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : t("memorial.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={() => { setAuthOpen(false); toast.success(t("memorial.messageDeposited")); }} />
      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onIndex={(i) => setLightbox((lb) => ({ ...lb, index: i }))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
